import React from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Not available";
const valueOrDash = (value) => value || "Not added";
const statusLabels = {
  pending_admin_review: "Pending Review",
  pending_verification: "Pending Verification",
};
const getJobStatusClass = (status) => {
  if (["closed", "declined", "expired"].includes(status)) return "danger";
  if (["paused", "pending_admin_review", "pending_verification"].includes(status)) return "warning";

  return "";
};

const AdminEmployerDetails = () => {
  const { employerId } = useParams();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + `/admin/employers/${employerId}`);
  const employer = response?.employer;
  const summary = employer?.summary ?? {};
  const profile = summary.company_profile ?? {};
  const stats = employer?.stats ?? {};
  const jobs = employer?.jobs ?? [];
  const applications = employer?.applications ?? [];
  const invoices = employer?.invoices ?? [];
  const activity = employer?.activity ?? [];

  const details = [
    { id: "epic-id", label: "Epic-Jobs ID", value: valueOrDash(summary.epic_jobs_id) },
    { id: "referral", label: "Referred By", value: summary.referred_by?.epic_jobs_id ? `${summary.referred_by.epic_jobs_id} (${summary.referred_by.name})` : "No referral" },
    { id: "company", label: "Company", value: valueOrDash(profile.company_name || summary.name) },
    { id: "owner", label: "Owner", value: valueOrDash(summary.name) },
    { id: "email", label: "Email", value: valueOrDash(summary.email) },
    { id: "phone", label: "Phone", value: valueOrDash(profile.phone) },
    { id: "location", label: "Location", value: valueOrDash(profile.location) },
    { id: "industry", label: "Industry", value: valueOrDash(profile.industry) },
    { id: "size", label: "Company Size", value: valueOrDash(profile.company_size) },
    { id: "plan", label: "Plan", value: valueOrDash(summary.plan_name) },
    { id: "credits", label: "Available Job Posts", value: summary.available_job_posts ?? 0 },
    { id: "joined", label: "Joined", value: formatDate(summary.created_at) },
    { id: "status", label: "Status", value: valueOrDash(summary.account_status) },
  ];

  const statCards = [
    { id: "jobs", label: "Jobs Posted", value: stats.jobs ?? 0 },
    { id: "active-jobs", label: "Active Jobs", value: stats.active_jobs ?? 0 },
    { id: "applications", label: "Applications", value: stats.applications ?? 0 },
    { id: "invoices", label: "Invoices", value: stats.invoices ?? 0 },
  ];

  return (
    <AdminPageShell
      active="employers"
      title={`${summary.name ?? "Employer"} Details`}
      text="Detailed employer account, company profile, jobs, applications, and activity."
    >
      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      {!error && !isLoading && (
        <div className="row">
          <div className="col-lg-7">
            <div className="ls-widget">
              <div className="widget-title">
                <h4>Employer Details</h4>
                <Link to="/admin-employers" className="theme-btn btn-style-three">Back</Link>
              </div>
              <div className="widget-content">
                <div className="admin-detail-grid">
                  {details.map((item) => (
                    <div className="admin-detail-item" key={item.id}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="row">
              {statCards.map((item) => (
                <div className="col-md-6" key={item.id}>
                  <div className="ui-item">
                    <div className="right">
                      <h4>{item.value}</h4>
                      <p>{item.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="ls-widget">
              <div className="widget-title">
                <h4>Job History</h4>
              </div>
              <div className="widget-content">
                <div className="admin-table-list">
                  {jobs.map((job) => (
                    <div className="admin-table-row" key={job.id}>
                      <div>
                        <strong>{job.title}</strong>
                        <span>{job.applications_count} applicants - {job.location} - {formatDate(job.application_deadline)}</span>
                      </div>
                      <span className={getJobStatusClass(job.status)}>{statusLabels[job.status] ?? job.status}</span>
                    </div>
                  ))}
                  {jobs.length === 0 && <div className="admin-empty-state">No jobs found for this employer.</div>}
                </div>
              </div>
            </div>

            <div className="ls-widget">
              <div className="widget-title">
                <h4>Recent Applicants</h4>
              </div>
              <div className="widget-content">
                <div className="admin-table-list">
                  {applications.map((application) => (
                    <div className="admin-table-row" key={application.id}>
                      <div>
                        <strong>{application.candidate_name}</strong>
                        <span>{application.job_title} - {application.candidate_email}</span>
                      </div>
                      <span>{application.status}</span>
                    </div>
                  ))}
                  {applications.length === 0 && <div className="admin-empty-state">No applications found for this employer.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="ls-widget">
              <div className="widget-title">
                <h4>Activity Timeline</h4>
              </div>
              <div className="widget-content">
                <div className="admin-history-list">
                  {activity.map((item) => (
                    <div className="admin-history-item" key={item.id}>
                      <span>{formatDate(item.date)}</span>
                      <strong>{item.title}</strong>
                      <p>{item.detail}</p>
                    </div>
                  ))}
                  {activity.length === 0 && <div className="admin-empty-state">No activity found.</div>}
                </div>
              </div>
            </div>

            <div className="ls-widget">
              <div className="widget-title">
                <h4>Invoice History</h4>
              </div>
              <div className="widget-content">
                <div className="admin-table-list">
                  {invoices.map((invoice) => (
                    <div className="admin-table-row" key={invoice.id}>
                      <div>
                        <strong>{invoice.invoice_number}</strong>
                        <span>{invoice.item_name} - {invoice.currency} {invoice.total}</span>
                      </div>
                      <span className={invoice.status === "failed" ? "danger" : ""}>{invoice.status}</span>
                    </div>
                  ))}
                  {invoices.length === 0 && <div className="admin-empty-state">No invoice records found.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default AdminEmployerDetails;
